// components/CustomTimePicker.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown, Edit2 } from 'lucide-react';

const CustomTimePicker = ({ 
  value = '', 
  onChange, 
  label, 
  required = false, 
  error = null,
  min = null,
  disabled = false,
  placeholder = "HH:MM"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [tempHours, setTempHours] = useState('');
  const [tempMinutes, setTempMinutes] = useState('');
  const [editValue, setEditValue] = useState('');
  const pickerRef = useRef(null);
  const inputRef = useRef(null);

  // Parse initial value
  useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      setHours(h);
      setMinutes(m);
      setEditValue(value);
    } else {
      setHours('');
      setMinutes('');
      setEditValue('');
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when editing mode is enabled
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const isValid24HourTime = (timeStr) => {
    if (!timeStr) return false;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeStr);
  };

  const validateAndSetTime = (h, m) => {
    let validHours = h;
    let validMinutes = m;

    // Validate hours (00-23)
    if (validHours !== '') {
      let hourNum = parseInt(validHours, 10);
      if (isNaN(hourNum)) {
        validHours = '';
      } else {
        hourNum = Math.min(23, Math.max(0, hourNum));
        validHours = hourNum.toString().padStart(2, '0');
      }
    }

    // Validate minutes (00-59)
    if (validMinutes !== '') {
      let minuteNum = parseInt(validMinutes, 10);
      if (isNaN(minuteNum)) {
        validMinutes = '';
      } else {
        minuteNum = Math.min(59, Math.max(0, minuteNum));
        validMinutes = minuteNum.toString().padStart(2, '0');
      }
    }

    // Check min time constraint
    if (min && validHours && validMinutes) {
      const currentTime = `${validHours}:${validMinutes}`;
      if (currentTime < min) {
        const [minH, minM] = min.split(':');
        setTempHours(minH);
        setTempMinutes(minM);
        return;
      }
    }

    setTempHours(validHours);
    setTempMinutes(validMinutes);
  };

  const handleHourChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      validateAndSetTime(value, tempMinutes);
    }
  };

  const handleMinuteChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      validateAndSetTime(tempHours, value);
    }
  };

  const incrementHour = () => {
    let currentHour = parseInt(tempHours || '0', 10);
    currentHour = (currentHour + 1) % 24;
    const newHour = currentHour.toString().padStart(2, '0');
    validateAndSetTime(newHour, tempMinutes);
  };

  const decrementHour = () => {
    let currentHour = parseInt(tempHours || '0', 10);
    currentHour = currentHour === 0 ? 23 : currentHour - 1;
    const newHour = currentHour.toString().padStart(2, '0');
    validateAndSetTime(newHour, tempMinutes);
  };

  const incrementMinute = () => {
    let currentMinute = parseInt(tempMinutes || '0', 10);
    currentMinute = (currentMinute + 1) % 60;
    const newMinute = currentMinute.toString().padStart(2, '0');
    validateAndSetTime(tempHours, newMinute);
  };

  const decrementMinute = () => {
    let currentMinute = parseInt(tempMinutes || '0', 10);
    currentMinute = currentMinute === 0 ? 59 : currentMinute - 1;
    const newMinute = currentMinute.toString().padStart(2, '0');
    validateAndSetTime(tempHours, newMinute);
  };

  const handleConfirm = () => {
    if (tempHours && tempMinutes) {
      const timeString = `${tempHours}:${tempMinutes}`;
      onChange(timeString);
      setHours(tempHours);
      setMinutes(tempMinutes);
      setEditValue(timeString);
      setIsOpen(false);
      setIsEditing(false);
    }
  };

  const handleEditChange = (e) => {
    let rawValue = e.target.value;
    
    // Allow only numbers and colon
    if (rawValue === '' || /^[\d:]*$/.test(rawValue)) {
      setEditValue(rawValue);
      
      // Auto-format when length is 2 or 3 characters
      if (rawValue.length === 2 && !rawValue.includes(':')) {
        const formatted = `${rawValue}:`;
        setEditValue(formatted);
        rawValue = formatted;
      }
      
      // Auto-complete when we have full time format
      if (rawValue.length === 5 && rawValue.includes(':')) {
        const [h, m] = rawValue.split(':');
        if (h.length === 2 && m.length === 2) {
          // Validate the complete time
          if (isValid24HourTime(rawValue)) {
            // Check min time constraint
            if (min && rawValue < min) {
              onChange(min);
              setEditValue(min);
              setHours(min.split(':')[0]);
              setMinutes(min.split(':')[1]);
            } else {
              onChange(rawValue);
              setHours(h);
              setMinutes(m);
            }
            setIsEditing(false);
          }
        }
      }
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Validate and apply the edited time
      if (isValid24HourTime(editValue)) {
        // Check min time constraint
        if (min && editValue < min) {
          onChange(min);
          setEditValue(min);
          setHours(min.split(':')[0]);
          setMinutes(min.split(':')[1]);
        } else {
          onChange(editValue);
          const [h, m] = editValue.split(':');
          setHours(h);
          setMinutes(m);
        }
        setIsEditing(false);
      } else if (editValue === '') {
        onChange('');
        setIsEditing(false);
      }
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value);
    }
  };

  const handleEditBlur = () => {
    // Validate and apply on blur
    if (editValue && isValid24HourTime(editValue)) {
      if (min && editValue < min) {
        onChange(min);
        setEditValue(min);
        setHours(min.split(':')[0]);
        setMinutes(min.split(':')[1]);
      } else {
        onChange(editValue);
        const [h, m] = editValue.split(':');
        setHours(h);
        setMinutes(m);
      }
    } else if (editValue === '') {
      onChange('');
    } else if (editValue && !isValid24HourTime(editValue)) {
      // Revert to last valid value
      setEditValue(value);
    }
    setIsEditing(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setTempHours(hours);
      setTempMinutes(minutes);
      setIsOpen(true);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      setIsEditing(true);
      setEditValue(value);
    }
  };

  const displayValue = value || '';

  return (
    <div className="w-full" ref={pickerRef}>
      {label && (
        <label className="block text-gray-800 font-bold mb-3 flex items-center gap-2">
          <Clock className="text-blue-600" size={18} />
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleEditChange}
            onKeyDown={handleEditKeyDown}
            onBlur={handleEditBlur}
            placeholder={placeholder}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition font-mono ${
              error 
                ? 'border-red-500 focus:border-red-600' 
                : 'border-blue-600 focus:border-blue-700'
            } bg-white`}
          />
        ) : (
          <div className="relative">
            <input
              type="text"
              value={displayValue}
              onClick={handleInputClick}
              readOnly
              disabled={disabled}
              placeholder={placeholder}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition cursor-pointer font-mono ${
                error 
                  ? 'border-red-500 focus:border-red-600' 
                  : 'border-gray-300 focus:border-blue-600'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-1">
              <button
                type="button"
                onClick={handleEditClick}
                disabled={disabled}
                className="p-1 hover:bg-gray-100 rounded transition disabled:opacity-50"
                title="Edit time directly"
              >
                <Edit2 size={16} className="text-gray-400" />
              </button>
              <Clock 
                size={20} 
                className="text-gray-400 pointer-events-none"
              />
            </div>
          </div>
        )}
      </div>

      {isOpen && !isEditing && (
        <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80">
          <div className="flex gap-4 justify-center mb-4">
            {/* Hours Selector */}
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-2">Hours (00-23)</div>
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={incrementHour}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronUp size={20} />
                </button>
                <input
                  type="text"
                  value={tempHours}
                  onChange={handleHourChange}
                  placeholder="HH"
                  maxLength={2}
                  className="w-20 text-center text-2xl font-mono font-bold py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={decrementHour}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronDown size={20} />
                </button>
              </div>
            </div>

            <div className="text-3xl font-bold text-gray-400 self-center">:</div>

            {/* Minutes Selector */}
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-2">Minutes (00-59)</div>
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={incrementMinute}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronUp size={20} />
                </button>
                <input
                  type="text"
                  value={tempMinutes}
                  onChange={handleMinuteChange}
                  placeholder="MM"
                  maxLength={2}
                  className="w-20 text-center text-2xl font-mono font-bold py-2 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={decrementMinute}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <ChevronDown size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!tempHours || !tempMinutes}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              OK
            </button>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              💡 Tip: Click the edit icon <Edit2 size={12} className="inline mx-1" /> to type time directly
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              Format: 24-hour (00:00 - 23:59)
            </p>
          </div>
        </div>
      )}

      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default CustomTimePicker;